import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";
import AdfImage from "./AdfImage";
import { modalSurfaceEffect } from "../../lib/modalSurface";
import { useI18n } from "../../lib/i18n";

/**
 * Full-screen gallery with zoom and swipe.
 *
 * The product page had neither. Navigation was `scrollIntoView` on a vertical
 * stack — no zoom, no lightbox, no touch handling anywhere — so a customer
 * could not see the weave of a fabric or the finish of a buttonhole. On a piece
 * priced at several hundred manat, that detail is the argument for the price.
 *
 * Swiping is native scroll-snap rather than a drag library: it inherits real
 * momentum, respects the platform's rubber-banding, and costs no bundle.
 * Zoom is a transform toggle with a pan offset taken from where the customer
 * actually clicked, so tapping a cuff magnifies the cuff rather than the middle
 * of the frame.
 */
export default function GalleryLightbox({ open, images = [], startIndex = 0, title = "", onClose }) {
  const { t } = useI18n();
  const trackRef = useRef(null);
  const closeRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const labelId = useId();
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(null); // { x: %, y: % } while magnified
  const [zoomLevel, setZoomLevel] = useState(2.4);
  // Refs so the native wheel listener (attached once) always reads the live
  // magnification and the last pointer position without re-binding.
  const zoomLevelRef = useRef(2.4);
  const pointerRef = useRef({ x: 50, y: 50 });

  const count = images.length;

  useEffect(() => modalSurfaceEffect(open), [open]);

  // Scroll lock without losing the reader's place: `position: fixed` on body
  // would jump the page to the top on close.
  useEffect(() => {
    if (!open) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    restoreFocusRef.current = document.activeElement;
    return () => {
      document.body.style.overflow = overflow;
      if (restoreFocusRef.current instanceof HTMLElement) restoreFocusRef.current.focus();
    };
  }, [open]);

  // Jump to the image that was clicked, before the first paint the customer
  // sees, so the lightbox never opens on the wrong picture and slides.
  useEffect(() => {
    if (!open) return;
    setIndex(startIndex);
    setZoom(null);
    const track = trackRef.current;
    if (track) track.scrollTo({ left: track.clientWidth * startIndex, behavior: "instant" });
    closeRef.current?.focus();
  }, [open, startIndex]);

  const go = useCallback((next) => {
    const track = trackRef.current;
    if (!track || !count) return;
    const clamped = Math.max(0, Math.min(count - 1, next));
    setZoom(null);
    track.scrollTo({ left: track.clientWidth * clamped, behavior: "smooth" });
    setIndex(clamped);
  }, [count]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") { event.preventDefault(); onClose?.(); }
      if (event.key === "ArrowRight") { event.preventDefault(); go(index + 1); }
      if (event.key === "ArrowLeft") { event.preventDefault(); go(index - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, go, onClose]);

  // Which slide is in view, so the counter and dots follow a finger swipe and
  // not only the arrow buttons.
  const onScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    const next = Math.round(track.scrollLeft / track.clientWidth);
    if (next !== index) { setIndex(next); setZoom(null); }
  };

  const originFromEvent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const point = event.touches?.[0] || event;
    return {
      x: ((point.clientX - rect.left) / rect.width) * 100,
      y: ((point.clientY - rect.top) / rect.height) * 100,
    };
  };

  const toggleZoom = (event) => {
    if (zoom) { setZoom(null); return; }
    setZoomLevel(2.4);
    zoomLevelRef.current = 2.4;
    const origin = originFromEvent(event);
    pointerRef.current = origin;
    setZoom(origin);
  };

  // Follow the pointer while magnified, so dragging across the frame pans the
  // zoomed image like a loupe rather than holding a fixed crop.
  const onFrameMove = (event) => {
    const origin = originFromEvent(event);
    pointerRef.current = origin;
    if (zoom) setZoom(origin);
  };

  // Reset the magnification whenever the zoom clears (a swipe, an arrow, the
  // toolbar toggle) so the next zoom starts at the base level.
  useEffect(() => {
    if (!zoom) { setZoomLevel(2.4); zoomLevelRef.current = 2.4; }
  }, [zoom]);

  // Scroll-wheel / trackpad-pinch zoom. A native, non-passive listener is the
  // only way to preventDefault the page/track from scrolling under the gesture.
  // Horizontal intent is left alone so a trackpad swipe still pages the gallery.
  useEffect(() => {
    if (!open) return undefined;
    const track = trackRef.current;
    if (!track) return undefined;
    const onWheel = (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      const next = Math.min(4.5, Math.max(1, zoomLevelRef.current + (event.deltaY < 0 ? 0.4 : -0.4)));
      if (next <= 1.02) { setZoom(null); return; }
      zoomLevelRef.current = next;
      setZoomLevel(next);
      setZoom({ ...pointerRef.current });
    };
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  }, [open]);

  if (!open || !count) return null;

  return (
    <div
      className="adf-lightbox"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
      data-testid="pdp-lightbox"
    >
      <h2 id={labelId} className="sr-only">{title}</h2>

      <div className="adf-lightbox__bar">
        <span className="adf-lightbox__count" data-testid="lightbox-counter">
          {index + 1} / {count}
        </span>
        <div className="adf-lightbox__tools">
          <button
            type="button"
            onClick={() => (zoom ? setZoom(null) : setZoom({ x: 50, y: 50 }))}
            aria-pressed={Boolean(zoom)}
            aria-label={zoom ? t("pdp.zoom_out", "Uzaqlaşdır") : t("pdp.zoom_in", "Yaxınlaşdır")}
            className="adf-lightbox__btn"
            data-testid="lightbox-zoom"
          >
            {zoom ? <ZoomOut size={19} strokeWidth={1.5} /> : <ZoomIn size={19} strokeWidth={1.5} />}
          </button>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("common.close", "Bağla")}
            className="adf-lightbox__btn"
            data-testid="lightbox-close"
          >
            <X size={19} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className={`adf-lightbox__track${zoom ? " is-zoomed" : ""}`}
        onScroll={onScroll}
      >
        {images.map((src, i) => (
          <figure key={`${src}-lb-${i}`} className="adf-lightbox__slide">
            <div
              className="adf-lightbox__frame"
              onClick={toggleZoom}
              onMouseMove={onFrameMove}
              onDoubleClick={(event) => event.preventDefault()}
              role="button"
              tabIndex={-1}
              aria-label={t("pdp.zoom_toggle", "Şəkli yaxınlaşdır")}
            >
              <AdfImage
                source={src}
                alt={`${title} — ${i + 1}`}
                sizes="100vw"
                priority={i === startIndex}
                className="adf-lightbox__img"
                style={zoom && i === index
                  ? { transform: `scale(${zoomLevel})`, transformOrigin: `${zoom.x}% ${zoom.y}%` }
                  : undefined}
              />
            </div>
          </figure>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(index - 1)}
            disabled={index === 0}
            aria-label={t("pdp.prev_image", "Əvvəlki şəkil")}
            className="adf-lightbox__nav is-prev"
          >
            <ChevronLeft size={22} strokeWidth={1.4} />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            disabled={index === count - 1}
            aria-label={t("pdp.next_image", "Növbəti şəkil")}
            className="adf-lightbox__nav is-next"
          >
            <ChevronRight size={22} strokeWidth={1.4} />
          </button>
          <div className="adf-lightbox__dots" aria-hidden="true">
            {images.map((src, i) => (
              <span key={`${src}-dot-${i}`} className={i === index ? "is-active" : ""} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
